import { Component, Event, EventEmitter, h, Prop, Build } from '@stencil/core';

/**
 * Binary toggle switch form control with an adjacent text label.
 * Emits a change event on interaction; supports disabled and required states.
 * Used in settings forms and pricing plan switchers (e.g. monthly/annual).
 *
 * @example
 *   <me-toggle name="notifications" label="Enable notifications"></me-toggle>
 */
@Component({
  tag: 'me-toggle',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Toggle {
  /**
   * The text label that appears next to the toggle
   * @default ''
   */
  @Prop() label: string = '';

  /**
   * The checked state of the toggle
   * @default false
   */
  @Prop({ mutable: true }) checked: boolean = false;

  /**
   * Whether the toggle is disabled
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * The name of the toggle when used in a form
   * @default ''
   */
  @Prop() name: string = '';

  /**
   * Whether the toggle is required in a form
   * @default false
   */
  @Prop() required: boolean = false;

  /**
   * Error state for toggle (set by form handler)
   */
  @Prop() error?: string;

  /**
   * Error message for toggle (set by form handler)
   */
  @Prop() errorMessage?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop() errorType?: 'required' | 'invalid';

  /**
   * Emitted when the toggle checked state changes
   * @event valueChange
   */
  @Event() valueChange: EventEmitter<boolean>;

  @Event() clearFieldError: EventEmitter<string>;

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = (this as any).hostEl?.parentElement || null;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }

  private handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.checked = input.checked;
    this.valueChange.emit(this.checked);
    this.clearFieldError.emit(this.getResolvedName());
    (this as any).hostEl?.removeAttribute('error');
    (this as any).hostEl?.removeAttribute('error-message');
    (this as any).hostEl?.removeAttribute('error-type');
  };

  private isClickOnLink(target: EventTarget | null): boolean {
    let el = target as HTMLElement | null;
    while (el) {
      if (el.tagName === 'A') return true;
      el = el.parentElement;
    }
    return false;
  }

  private handleClick = (event: MouseEvent) => {
    if (this.disabled) return;
    // If click was on a link, do not toggle toggle
    if (this.isClickOnLink(event.target)) {
      return;
    }
    const label = event.currentTarget as HTMLLabelElement;
    const input = label.querySelector('input') as HTMLInputElement;
    if (input) {
      input.checked = !input.checked;
      this.checked = input.checked;
      this.valueChange.emit(this.checked);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) return;
    const active = Build.isBrowser ? document.activeElement : null;
    if (active && active.tagName === 'A' && event.key === 'Enter') {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.checked = !this.checked;
      this.valueChange.emit(this.checked);
      this.clearFieldError.emit(this.getResolvedName());
    }
  };

  render() {
    // Generate id only if both label and name are present
    let inputId: string | undefined = undefined;
    if (this.label && this.name) {
      if (Build.isBrowser && !document.getElementById(this.name)) {
        inputId = this.name;
      } else {
        inputId = this.getResolvedName();
      }
    }
    const hasError = this.error === 'true';
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      if (this.errorType === 'required') errorText = this.requiredMessage || 'This field is required';
      else if (this.errorType === 'invalid') errorText = this.invalidMessage || 'Invalid value';
      else errorText = 'This field is invalid';
    }
    const resolvedName = this.getResolvedName();
    if ((this as any).hostEl) {
      (this as any).hostEl.setAttribute('name', resolvedName);
    }
    return (
      <label
        class={{
          'toggle': true,
          'toggle--checked': this.checked,
          'toggle--disabled': this.disabled,
          'toggle--error': hasError,
        }}
        onClick={this.handleClick}
        onKeyDown={this.handleKeyDown}
        role="presentation"
      >
        <input
          type="checkbox"
          class={{
            'toggle__input': true,
            'toggle__input--error': hasError,
          }}
          {...(inputId ? { id: inputId } : {})}
          name={resolvedName}
          checked={this.checked}
          disabled={this.disabled}
          required={this.required}
          onChange={this.handleChange}
          tabIndex={this.disabled ? -1 : 0}
          role="switch"
          aria-checked={this.checked ? 'true' : 'false'}
          aria-disabled={this.disabled ? 'true' : 'false'}
        />

        <span class="toggle__switch">
          <span class="toggle__handle"></span>
        </span>

        {this.label && <span class="toggle__label">{this.label}</span>}
        {hasError && errorText && (
          <me-text variant="small" class="form-error-message">
            {errorText}
          </me-text>
        )}
      </label>
    );
  }
}
